import { useCallback } from 'react';
import { useQuery } from 'react-query';
import { FormsQueryKeys } from '@frontend/api-forms';
import { PersonAPI, PersonTypes } from '@frontend/api-person';
import { ContactNumberType } from '../types';

interface ContactNumber {
  type: ContactNumberType;
  value: string;
}

type GetContactNumbersFn = () => ContactNumber[];

interface UseContactDetailsResults {
  getContactNumbers: GetContactNumbersFn;
  selectedContact: PersonTypes.Person | undefined;
  isLoadingContact: boolean;
}

const EMPTY_CONTACT_NUMBERS: ContactNumber[] = [];

export const useContactDetails = (personId: string, locationId: string): UseContactDetailsResults => {
  const { data: selectedContact, isLoading: isLoadingContact } = useQuery({
    queryKey: [FormsQueryKeys.sendForms.contact, personId, locationId],
    queryFn: () => PersonAPI.getPersonExtended(personId, locationId),
    enabled: !!personId,
  });

  const getContactNumbers = useCallback<GetContactNumbersFn>(() => {
    if (!selectedContact) {
      return EMPTY_CONTACT_NUMBERS;
    }

    const contactNumbers: ContactNumber[] = [];
    if (selectedContact.MobilePhone) {
      contactNumbers.push({
        type: ContactNumberType.MOBILE,
        value: selectedContact.MobilePhone,
      });
    }

    if (selectedContact.HomePhone) {
      contactNumbers.push({
        type: ContactNumberType.HOME,
        value: selectedContact.HomePhone,
      });
    }

    if (selectedContact.WorkPhone) {
      contactNumbers.push({
        type: ContactNumberType.WORK,
        value: selectedContact.WorkPhone,
      });
    }

    return contactNumbers;
  }, [selectedContact]);

  return {
    selectedContact,
    isLoadingContact,
    getContactNumbers,
  };
};
